import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    MenuItem,
    Pagination,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PageTitle from '../shared/PageTitle';
import PageArea from '../shared/PageArea';
import LoadingWheel from '../shared/LoadingWheel';
import UserFormDialog, { UserFormValues } from './UserFormDialog';
import { UsersClient, RolesClient, UserDto, RoleSummaryDto } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../contexts/PermissionContext';

const pageSizeOptions = [10, 25, 50];

const UsersPage: React.FC = () => {
    const { t } = useTranslation();
    const { userId: currentUserId } = usePermissions();
    const usersClient = useMemo(() => new UsersClient(getApiBaseUrl(), authenticatedFetch), []);
    const rolesClient = useMemo(() => new RolesClient(getApiBaseUrl(), authenticatedFetch), []);

    const [users, setUsers] = useState<UserDto[]>([]);
    const [roles, setRoles] = useState<RoleSummaryDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [formState, setFormState] = useState<{ open: boolean; mode: 'create' | 'edit'; userId?: string } | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [dialogValues, setDialogValues] = useState<UserFormValues | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingUser, setIsLoadingUser] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<UserDto | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await usersClient.getApiUsers(pageNumber, pageSize, searchTerm || undefined);
            setUsers(response.items ?? []);
            setTotalPages(Math.max(response.totalPages ?? 1, 1));
            setTotalCount(response.totalCount ?? 0);
        } catch (err) {
            const message = err instanceof Error ? err.message : t('users.errors.load');
            setError(message);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [pageNumber, pageSize, searchTerm, usersClient, t]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPageNumber(1);
            setSearchTerm(searchInput.trim());
        }, 400);

        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        const loadRoles = async () => {
            try {
                const rolesData = await rolesClient.getApiRoles(1, 100, undefined);
                setRoles(rolesData.items ?? []);
            } catch (err) {
                console.error('Failed to load roles', err);
            }
        };

        loadRoles();
    }, [rolesClient]);

    const ensureDataLoaded = () => {
        if (roles.length === 0) {
            setNotification({ severity: 'error', message: t('users.errors.dataUnavailable') });
            return false;
        }
        return true;
    };

    const handleOpenCreate = () => {
        if (!ensureDataLoaded()) {
            return;
        }

        setFormError(null);
        setDialogValues(null);
        setFormState({ open: true, mode: 'create' });
    };

    const handleOpenEdit = async (user: UserDto) => {
        if (!ensureDataLoaded()) {
            return;
        }

        setFormError(null);
        setDialogValues(null);
        if (!user.id) {
            setFormError(t('users.errors.loadUser'));
            return;
        }

        setFormState({ open: true, mode: 'edit', userId: user.id });
        setIsLoadingUser(true);

        try {
            const details = await usersClient.getApiUsersAdmin(user.id);
            setDialogValues({
                email: details.email ?? '',
                firstName: details.firstName ?? '',
                lastName: details.lastName ?? '',
                companyName: details.companyName ?? '',
                roleIds: details.roleIds ?? [],
            });
        } catch (err) {
            const message = err instanceof Error ? err.message : t('users.errors.loadUser');
            setFormError(message);
        } finally {
            setIsLoadingUser(false);
        }
    };

    const closeDialog = () => {
        setFormState(null);
        setFormError(null);
        setDialogValues(null);
        setIsSaving(false);
        setIsLoadingUser(false);
    };

    const handleSaveUser = async (values: UserFormValues) => {
        if (!formState) {
            return;
        }

        setIsSaving(true);
        setFormError(null);

        try {
            if (formState.mode === 'create') {
                await usersClient.postApiUsers({
                    email: values.email,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    companyName: values.companyName,
                    roleIds: values.roleIds,
                });
                setNotification({ severity: 'success', message: t('users.feedback.createSuccess') });
            } else if (formState.userId) {
                await usersClient.putApiUsers(formState.userId, {
                    email: values.email,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    companyName: values.companyName,
                    roleIds: values.roleIds,
                });
                setNotification({ severity: 'success', message: t('users.feedback.updateSuccess') });
            }

            closeDialog();
            await loadUsers();
        } catch (err) {
            const message = err instanceof Error ? err.message : t('users.errors.save');
            setFormError(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenDelete = (user: UserDto) => {
        if (user.id && currentUserId && user.id === currentUserId) {
            setNotification({ severity: 'error', message: t('users.errors.deleteSelf') });
            return;
        }

        setDeleteTarget(user);
        setDeleteError(null);
    };

    const handleDeleteUser = async () => {
        if (!deleteTarget) {
            return;
        }

        setIsDeleting(true);
        setDeleteError(null);

        try {
            if (!deleteTarget.id) {
                setDeleteError(t('users.errors.delete'));
                return;
            }

            if (currentUserId && deleteTarget.id === currentUserId) {
                setDeleteError(t('users.errors.deleteSelf'));
                return;
            }

            await usersClient.deleteApiUsers(deleteTarget.id);
            setNotification({ severity: 'success', message: t('users.feedback.deleteSuccess') });
            setDeleteTarget(null);
            await loadUsers();
        } catch (err) {
            const message = err instanceof Error ? err.message : t('users.errors.delete');
            setDeleteError(message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = Number(event.target.value);
        setPageSize(value);
        setPageNumber(1);
    };

    const pagedDescription = useMemo(
        () =>
            t('users.pagination.summary', {
                count: totalCount,
            }),
        [t, totalCount]
    );

    if (loading) {
        return <LoadingWheel />;
    }

    return (
        <PageArea fullHeight>
            <PageTitle title={t('users.title')} subtitle={t('users.subtitle')} />
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 3, gap: 2 }}
            >
                <TextField
                    placeholder={t('users.searchPlaceholder')}
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    fullWidth
                />

                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        select
                        label={t('users.pageSize')}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        sx={{ minWidth: 140 }}
                    >
                        {pageSizeOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                        size="lg"
                        sx={{ minWidth: 200 }}
                    >
                        {t('users.actions.add')}
                    </Button>
                </Stack>
            </Stack>

            {error && (
                <Alert variant='filled' severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {users.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="textMD" color="text.secondary">
                        {t('users.emptyState')}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('users.table.name')}</TableCell>
                                        <TableCell>{t('users.table.email')}</TableCell>
                                        <TableCell>Company</TableCell>
                                        <TableCell>{t('users.table.roles')}</TableCell>
                                        <TableCell align="right">{t('users.table.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user) => {
                                        const userRoles = user.roles ?? [];
                                        const displayedRoles = userRoles.slice(0, 3);
                                        const hiddenRoleCount = Math.max(userRoles.length - displayedRoles.length, 0);
                                        const isCurrentUser = Boolean(user.id && currentUserId && user.id === currentUserId);

                                        return (
                                            <TableRow
                                                key={user.id}
                                                hover
                                                onClick={() => handleOpenEdit(user)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell variant="clampedText">
                                                    <Typography variant="textMD" sx={{ fontWeight: 600 }}>
                                                        {`${user.firstName} ${user.lastName}`}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell variant="clampedText">
                                                    <Typography variant="textSM" color="text.secondary">
                                                        {user.email}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell variant="clampedText">
                                                    <Typography variant="textSM" color="text.secondary">
                                                        {user.companyName || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell variant="clampedText">
                                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                        {displayedRoles.map((role) => (
                                                            <Chip key={role.roleId} label={role.roleName} size="small" variant="outlined" />
                                                        ))}
                                                        {hiddenRoleCount > 0 && (
                                                            <Chip
                                                                label={t('users.moreRoles', { count: hiddenRoleCount })}
                                                                size="small"
                                                                color="primary"
                                                            />
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Tooltip title={t('common.edit')}>
                                                            <Button
                                                                variant="secondaryIconOnly"
                                                                size="lg"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    handleOpenEdit(user);
                                                                }}
                                                            >
                                                                <EditIcon />
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip title={t('common.delete')}>
                                                            <Button
                                                                variant="badIconOnly"
                                                                size="lg"
                                                                disabled={isCurrentUser}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    handleOpenDelete(user);
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </Button>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>

                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" justifyContent="space-between" sx={{ mt: 3 }} spacing={2}>
                        <Pagination
                            count={totalPages}
                            page={Math.min(pageNumber, totalPages)}
                            onChange={(_, page) => setPageNumber(page)}
                            color="primary"
                        />
                        <Typography variant="textSM" color="text.secondary">
                            {pagedDescription}
                        </Typography>
                    </Stack>
                </>
            )}

            <UserFormDialog
                open={Boolean(formState?.open)}
                title={formState?.mode === 'edit' ? t('users.dialog.editTitle') : t('users.dialog.createTitle')}
                roles={roles}
                initialValues={dialogValues ?? undefined}
                isSubmitting={isSaving}
                isLoading={formState?.mode === 'edit' && isLoadingUser}
                errorMessage={formError}
                mode={formState?.mode ?? 'create'}
                onClose={closeDialog}
                onSubmit={handleSaveUser}
            />

            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon color="warning" />
                    {t('users.delete.title')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="textMD" sx={{ mb: 2 }}>
                        {t('users.delete.message', { name: `${deleteTarget?.firstName} ${deleteTarget?.lastName}` })}
                    </Typography>
                    {deleteError && (
                        <Alert variant='filled' severity="error">{deleteError}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="secondary" size="lg" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="bad" size="lg" onClick={handleDeleteUser} disabled={isDeleting}>
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {notification && (
                <Snackbar
                    open
                    autoHideDuration={4000}
                    onClose={() => setNotification(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        severity={notification.severity}
                        variant="filled"
                        onClose={() => setNotification(null)}
                        sx={{ width: '100%' }}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
            )}
        </PageArea>
    );
};

export default UsersPage;
